import { handleEmptyObjects } from '../handleEmptyObjects/handleEmptyObjects';

/**
 * Utility for printing objects that should only be displayed in dev mode.
 *
 * @example
 * // prints a string
 * devLogger( '[User] Session confirmed'  )
 *
 * @example
 * // prints an object in a group, collapsed or open
 * devLogger({ title: 'User', value: myObject, collapsed: true })
 *
 * @param {object} data - Log input
 * @param {string} data.topic - Log topic
 * @param {string} data.title - Log title
 * @param {object} data.value - Log value, should be an object
 * @param {number} level - Log level
 * @param {string} level - Log level
 */
export const devLogger = ( data, level, topic ) => {
  const hasTopic = data?.topic || topic;
  const debugTopic = hasTopic && logTopic( { topic: hasTopic } );
  const shouldLog = hasTopic ? debugTopic : isDevMode();

  if( !shouldLog ){
    return;
  }

  if( typeof data === 'string' ){
    const styles = [
      'background: #fdf1f3',
      'color: #793025',
      'padding: 4px',
      'border-radius: 4px',
      'font-weight: bold'
    ].join( ';' );

    // eslint-disable-next-line no-console
    console.log( '%c%s', level > 0 ? styles : '', data );
  }
  else if( data && typeof data === 'object' ){
    objectLogger( data );
  }
  else {
    // eslint-disable-next-line no-console
    console.log( data );
  }
};

const formatLoggerTitle = ( scope, message ) => {
  if( !scope ){
    return message;
  }

  if( !message ){
    return scope;
  }

  return `${scope} ${message}`;
};

export const createLogger = ( data = {}, methods = {} ) => {
  const {
    topic,
    scope,
    collapsed = true
  } = handleEmptyObjects( data );
  const { log = devLogger } = handleEmptyObjects( methods );

  const logAtLevel = ( level = 0 ) => ( message, value, options = {} ) => {
    const title = formatLoggerTitle( scope, message );
    const { collapsed: overrideCollapsed } = handleEmptyObjects( options );

    if( typeof value === 'undefined' ){
      log( title, level, topic );
      return;
    }

    log( {
      topic,
      title,
      value,
      collapsed: overrideCollapsed ?? collapsed
    }, level, topic );
  };

  return {
    log: logAtLevel(),
    info: logAtLevel(),
    warn: logAtLevel( 1 ),
    error: logAtLevel( 1 )
  };
};

export const debugModuleName = ( props ) => {
  const { moduleName, modules } = props || {};

  if( moduleName === 'StateWrapper' ){
    return `StateWrapper > ${modules?.[0]?.moduleName}`;
  }

  return moduleName;
};

/**
 * Logging util, crawls objects for output
 * @param {string} title Title of log section
 * @param {object} obj Object to log
 */
export const objectLogger = ( data ) => {
  if( !isDevMode() ){
    return;
  }

  const {
    collapsed = true,
    title: existingTitle,
    value: existingValue
  } = handleEmptyObjects( data );

  const guess = { ...handleEmptyObjects( data ) };
  delete guess.title;
  delete guess.collapsed;
  delete guess.topic;

  const title = existingTitle ?? Object.keys( guess )?.[0];
  const hasValue = Object.prototype.hasOwnProperty.call( handleEmptyObjects( data ), 'value' );
  const value = hasValue ? existingValue : Object.values( guess )?.[0];

  if( title === undefined || title === null || title === '' ){
    return;
  }

  if( value === undefined || value === null || typeof value !== 'object' ){
    // eslint-disable-next-line no-console
    console.log( `${title}:`, value );
    return;
  }

  // eslint-disable-next-line no-console
  collapsed ? console.groupCollapsed( title ) : console.group( title );

  try {
    // eslint-disable-next-line no-console
    console.log( value );
  }
  finally {
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

/**
 * @returns {boolean} Allows logging / dev mode activity to be conditionally enabled
 */
export const isDevMode = () => process.env.NODE_ENV === 'development' || global.location?.search?.includes( ENABLE_LOG_PARAM );

/**
 * Checks if a topic is enabled for logging
 *
 * @param {object} data - arguments
 * @param {string} data.topic - topic to check
 * @returns {boolean}
 */
export const logTopic = ( data ) => {
  const { topic } = data || {};

  if( !topic ){
    return false;
  }

  const globalLocation = global?.location || {};
  const search = globalLocation?.search;
  const isAll = search?.includes( `log=${LOG_TOPIC.All}` ) && LOG_TOPIC_ALL.includes( topic );

  if( isAll ){
    return true;
  }

  return search?.includes( `log=${topic}` );
};

export const LOG_TOPIC = {
  All: 'all',
  Broadcast: 'broadcast',
  Cache:'cache',
  DependencyTracker: 'deps',
  DXL: 'dxl',
  InitAction: 'initAction',
  RequestStack: 'requestStack',
  ResourceLoader: 'resourceLoader',
  Sdk: 'sdk',
  Session: 'session',
  ThirdParty: 'thirdParty'
};

export const LOG_TOPIC_ALL = [
  // LOG_TOPIC.DXL,
  LOG_TOPIC.InitAction,
  LOG_TOPIC.Session,
  LOG_TOPIC.ThirdParty,
  LOG_TOPIC.DependencyTracker,
  LOG_TOPIC.Sdk
];

/**
 * @const {string} ENABLE_LOG_PARAM - URL param to enable logging
 */
export const ENABLE_LOG_PARAM = 'log=';
